import React, { useState, useEffect } from "react";
import { headerStyle, sectionStyle } from "./formConstants";
import { FormCheckBox, NormalFormDate, FormInput, FormTitle, FormsubTitle } from "./common";
import { useSharedValue } from "../components/Constants/constants";
import "./style.css";
import CountryDropdown from "./CountryDropdown";
import { useSelfCertEntityForm } from "../Hooks/useSelfCertEntityForm";
import { SelfCertEntityService } from "../services/SelfCertEntityService";

interface SelfCertFormData {
  [key: string]: string | boolean | Array<any>;
}

interface SelfCertFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const SelfCertEntityForm = ({
  selectedRowData,
  row,
  selectedIndex = 0,
  onSaveSuccess,
}: SelfCertFormProps) => {
  // Add a stable key to prevent component re-mounting
  const componentKey = `self-cert-entity-${selectedRowData?.UniqueIdentifier || "default"}`;

  const { isMenuOpen } = useSharedValue();

  // Use the hook for form management
  const { model, isSaving, updateField, updateMultipleFields, validate } =
    useSelfCertEntityForm(selectedRowData);

  // Helper function to get field values from model
  const getFieldValue = (fieldName: string): any => {
    return model[fieldName] || "";
  };

  // Form state

  const [lastSavedData, setLastSavedData] = useState<any>(null);

  // No need for jurisdictionRows state - using static 3-row table

  const [formData, setFormData] = useState<SelfCertFormData>(() => {
    // Create initial state with all provided keys
    const initialState: SelfCertFormData = {
      UniqueIdentifier: "",

      // Form Information
      Form_name: "",

      // Section 1 - Entity Information
      "SC-Entity-Section_1_entity_name": "",
      "SC-Entity-Section_1_org_country": "",

      // Section 1 - Current Residence Address
      "Permanent residence address": "",
      "SC-Entity-Section_1_current_residence_address_street": "",
      "SC-Entity-Section_1_current_residence_address_city": "",
      "SC-Entity-Section_1_current_residence_address_state": "",
      "SC-Entity-Section_1_current_residence_address_country": "",
      "Permanent address country": "",
      "SC-Entity-Section_1_current_residence_address_postal": "",

      // Section 1 - Mailing Address
      "Mailing address": "",
      "SC-Entity-Section_1_mailing_address_street": "",
      "Mailing address city": "",
      "SC-Entity-Section_1_mailing_address_city": "",
      "SC-Entity-Section_1_mailing_address_state": "",
      "SC-Entity-Section_1_mailing_address_country": "",
      "Mailing address country": "",
      "SC-Entity-Section_1_mailing_address_postal": "",

      // Section 2 - Tax Residency
      "SC-Entity-Section_2_tax_identification_country": "",
      "SC-Entity-Section_2_tax_reference_number_type": "",
      "SC-Entity-Section_2_tax_id": "",
      "SC-Entity-Section_2_reason_for_non_tin": "",
      "SC-Entity-Section_2_unable_to_procure_a_tin": false,

      "SC-Entity-Section_2_sole_residency_yes": "",
      "SC-Entity-Section_2_sole_residency_no": "",
      "SC-Entity-Section_2_tax_purposes": [],

      // Additional Tax Residency Fields
      "sc_11 Jurisdiction of Tax Residence2": "",
      "sc_11 Tax Identification Number2": "",
      "sc_11 Tax Reference Number Type2": "",
      "sc_11 non-availability of TIN 2": "",
      "sc_11 Jurisdiction of Tax Residence3": "",
      "sc_11 Tax Identification Number3": "",
      "sc_11 Tax Reference Number Type3": "",
      "sc_11 non-availability of TIN 3": "",
      "sc_11 Jurisdiction of Tax Residence4": "",
      "sc_11 Tax Identification Number4": "",
      "sc_11 Tax Reference Number Type4": "",
      "sc_11 non-availability of TIN 4": "",

      // Section 3 - FATCA and CRS Classification
      "SC-Entity-Section_3_financial_institution": "",
      "SC-Entity-Section_3_fatca_purposes": "",
      "SC-Entity-Section_3_reporting_financial_institution_under_crs": "",
      "SC-Entity-Section_3_non_financial_institution_under_crs": "",
      "SC-Entity-Section_3_goverment_entity": "",
      "SC-Entity-Section_3_international_org": "",
      "SC-Entity-Section_3_central_bank": "",
      "SC-Entity-Section_3_broad_participation_retirement_fund": "",
      "SC-Entity-Section_3_narrow_participation_retirement_fund": "",
      "SC-Entity-Section_3_pension_fund": "",
      "SC-Entity-Section_3_exempt_collective_investment": "",
      "SC-Entity-Section_3_trust_trustee": "",
      "SC-Entity-Section_3_qualified_credit_card_issuer": "",
      "SC-Entity-Section_3_other_entity": "",
      "SC-Entity-Section_3_non_financial_institution_under_crs_resident": "",
      "SC-Entity-Section_3_investment_entity_managed_by_company_listed": "",
      "SC-Entity-Section_3_investment_entity": "",
      "SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ": "",
      "SC-Entity-Section_3_investment_entity_managed_by_pension_fund": "",
      "SC-Entity-Section_3_other_investment_entity": "",
      "SC-Entity-Section_3_non_financial_institution": "",
      "SC-Entity-Section_3_active_nfe": "",
      "SC-Entity-Section_3_regularly_traded_corporation": "",
      "SC-Entity-Section_3_stock_exchange_name": "",
      "SC-Entity-Section_3_regularly_traded_corporation_name": "",
      "SC-Entity-Section_3_active_nfe_goverment_entity": "",
      "SC-Entity-Section_3_active_nfe_international_org": "",
      "SC-Entity-Section_3_active_nfe_other": "",
      "SC-Entity-Section_3_passive_nfe": "",
      "SC-Entity-Section_3_controlling_persons": [],

      // CRS Classification Fields (used in form)
      "sc_8 Financial Institution": false,
      "sc_8 GIIN for FATCA purposes": "",
      "sc_8a Reporting Financial": false,
      "sc_8b Non-Reporting": false,
      "sc_8 Governmental Entity": false,
      "sc_8 International Organization": false,
      "sc_8 Central Bank": false,
      "sc_8 Broad Participation Retirement Fund": false,
      "sc_8 Narrow Participation": false,
      "sc_8 Pension Fund": false,
      "sc_8 Exempt Collective": false,
      "sc_8 Trust whose trustee": false,
      "sc_8 Qualified Credit Card Issuer": false,
      "sc_8 Other Entity": false,
      "sc_8 Other Entity Text": "",
      "sc_9 Active Non-Financial Entity": false,
      "sc_9 Corporation traded": false,
      "sc_9 Stock exchange name": "",
      "sc_9 Related entity name": "",
      "sc_9 Governmental Entity": false,
      "sc_9 International Organization": false,
      "sc_9 Other Active NFE": false,
      "sc_10 Investment Entity": false,
      "sc_10 Investment Entity managed by Financial Institution": false,
      "sc_10 Investment Entity managed by Non-Financial Entity": false,
      "sc_10 Investment Entity managed by company listed": false,
      "sc_10 Investment Entity managed by widely held investment vehicle": false,
      "sc_10 Investment Entity managed by pension fund": false,
      "sc_10 Other Investment Entity": false,
      "sc_11 Passive Non-Financial Entity": false,
      "sc_13 Non-Financial Institution": false,
      "sc_13 Active Non-Financial Entity": false,
      "sc_13 Passive Non-Financial Entity": false,
      "sc_14 Other Classification": false,
      "sc_14 Other Classification Text": "",
      "sc_Account Holder1": "",
      "sc_Account Holder2": "",
      "sc_Account Holder3": "",
      "sc_Account Holder4": "",

      // Section 4 - Signature and Controlling Person
      "SC-Entity-Section_4_signature": "",
      "SC-Entity-Section_4_signer_printed_name": "",
      "SC-Entity-Section_4_signature_date": "",
      "SC-Entity-Section_4_capacity": "",
      "SC-Entity-isSigned": "no",
      "SC-Entity-isModified": "no",

      // Section IV Declaration Fields
      sc_consent_to_information_sharing: false,
      sc_agree_to_inform_changes: false,
      sc_certify_account_holder: false,
      sc_declare_statements_correct: false,

      // Entity and Controlling Person Information
      "SC-Entity-Legal_entity": "",
      "SC-Entity-Controlling_person_title": "",
      "SC-Entity-Controlling_person_last_name": "",
      "SC-Entity-Controlling_person_first_name": "",
      "SC-Entity-Controlling_person_middle_name": "",
      "SC-Entity-Controlling_person_city_of_birth": "",
      "SC-Entity-Controlling_person_country_of_birth": "",
      "SC-Entity-Controlling_person_dob": "",
      "SC-Entity-Controlling_person_address_street": "",
      "SC-Entity-Controlling_person_address_city": "",
      "SC-Entity-Controlling_person_address_state": "",
      "SC-Entity-Controlling_person_address_country": "",
      "SC-Entity-Controlling_person_address_postal": "",
      "SC-Entity-Controlling_person_mailing_address_street": "",
      "SC-Entity-Controlling_person_mailing_address_city": "",
      "SC-Entity-Controlling_person_mailing_address_state": "",
      "SC-Entity-Controlling_person_mailing_address_country": "",
      "SC-Entity-Controlling_person_mailing_address_postal": "",
      "SC-Entity-Controlling_person_type_owner": false,
      "SC-Entity-Controlling_person_owner_percentage": "",
      "SC-Entity-Controlling_person_by_other_means": false,
      "SC-Entity-Controlling_person_senior_managing": false,
      "SC-Entity-Controlling_person_settlor": false,
      "SC-Entity-Controlling_person_trustee": false,
      "SC-Entity-Controlling_person_protector": false,
      "SC-Entity-Controlling_person_beneficiary": false,
      "SC-Entity-Controlling_person_other": false,
      "SC-Entity-Controlling_person_settlor_equivalent": "",
      "SC-Entity-Controlling_person_trustee_equivalent": "",
      "SC-Entity-Controlling_person_protector_equivalent": "",
      "SC-Entity-Controlling_person_beneficiary_equivalent": "",
      "SC-Entity-Controlling_person_other_equivalent": "",

      // Section 4 - Tax Identification for Controlling Person
      "SC-Entity-Section_4_jurisdiction_1": "",
      "SC-Entity-Section_4_tax_reference_number": "",
      "SC-Entity-Section_4_tax_id": "",
      "SC-Entity-Section_4_sole_residency_yes": false,
      "SC-Entity-Section_4_sole_residency_no": false,
      "SC-Entity-Section_4_unable_to_procure_tin": false,
      "SC-Entity-Section_4_reason_non_availability_of_tin": "",
      "SC-Entity-Section_4_tax_purposes": [],

      // Section 5 - Controlling Person Signature
      "SC-Entity-Section_5_signature": "",
      "SC-Entity-Section_5_signer_printed_name": "",
      "SC-Entity-Section_5_signature_date": "",
      "SC-Entity-Section_5_capacity": "",
    };

    // If selectedRowData exists, update the initial state
    if (selectedRowData) {
      Object.keys(initialState).forEach((key) => {
        // Try exact match first
        let matchingKey = Object.keys(selectedRowData).find(
          (rowKey) => rowKey.toLowerCase() === key.toLowerCase()
        );

        // If no exact match, try converting underscores to hyphens
        if (!matchingKey) {
          const keyWithHyphens = key.replace(/_/g, "-");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === keyWithHyphens.toLowerCase()
          );
        }

        // If still no match, try converting hyphens to underscores
        if (!matchingKey) {
          const keyWithUnderscores = key.replace(/-/g, "_");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === keyWithUnderscores.toLowerCase()
          );
        }

        if (matchingKey) {
          // Convert "selected" values to true, empty strings to false for boolean fields
          if (typeof initialState[key] === "boolean") {
            initialState[key] =
              selectedRowData[matchingKey] === "selected" || selectedRowData[matchingKey] === true;
          } else {
            initialState[key] = selectedRowData[matchingKey];
          }
        } else {
          // console.log(`❌ Self-Cert Entity - No match found for key: ${key}`);
        }
      });
    }

    return initialState;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    // Update the model using the hook
    updateField(name, fieldValue);
  };

  // No need for add/remove jurisdiction functions - using static 3-row table

  // Handle data extraction and mapping when selectedRowData changes
  useEffect(() => {
    if (selectedRowData && !isSaving) {
      // Don't update if this is the same data we just saved
      if (lastSavedData && JSON.stringify(selectedRowData) === JSON.stringify(lastSavedData)) {
        // console.log("🔄 Self-Cert Entity - Skipping update, same as last saved data");
        return;
      }

      const updatedFormData = { ...formData };
      let hasUpdates = false;

      // Map all form fields
      Object.keys(formData).forEach((key) => {
        // Try exact match first
        let matchingKey = Object.keys(selectedRowData).find(
          (rowKey) => rowKey.toLowerCase() === key.toLowerCase()
        );

        // If no exact match, try converting underscores to hyphens
        if (!matchingKey) {
          const keyWithHyphens = key.replace(/_/g, "-");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === keyWithHyphens.toLowerCase()
          );
        }

        // If still no match, try converting hyphens to underscores
        if (!matchingKey) {
          const keyWithUnderscores = key.replace(/-/g, "_");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === keyWithUnderscores.toLowerCase()
          );
        }

        // Try SC-Entity- prefix mapping
        if (!matchingKey && key.startsWith("SC-Entity-")) {
          const scEntityKey = key.replace("SC-Entity-", "SC-Entity-");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === scEntityKey.toLowerCase()
          );
        }

        // Try sc_ prefix mapping
        if (!matchingKey && key.startsWith("sc_")) {
          const scKey = key.replace("sc_", "sc-");
          matchingKey = Object.keys(selectedRowData).find(
            (rowKey) => rowKey.toLowerCase() === scKey.toLowerCase()
          );
        }

        if (matchingKey && selectedRowData[matchingKey] !== undefined) {
          let value = selectedRowData[matchingKey];

          // Convert "selected" values to true, empty strings to false for boolean fields
          if (typeof formData[key] === "boolean") {
            value = value === "selected" || value === true || value === "true" || value === 1;
          }

          if (updatedFormData[key] !== value) {
            updatedFormData[key] = value;
            hasUpdates = true;
          }
        }
      });

      if (hasUpdates) {
        setFormData(updatedFormData);
      }
    }
  }, [selectedRowData, isSaving, lastSavedData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Use the model as the source of truth
      const currentFormValues = { ...model };
      currentFormValues.isMapDone = true;

      // Create save model with all form data
      const saveModel = new (model.constructor as any)(currentFormValues);

      // Save using the service
      const result = await SelfCertEntityService.saveToApi(saveModel, row, selectedIndex);

      if (result.success) {
        if (onSaveSuccess) {
          onSaveSuccess(result.updatedRow);
        }
        alert("Form saved successfully");
      } else {
        console.error("❌ SelfCertEntity Failed to save form:", result.error);
        alert("Failed to save form: " + result.error);
      }
    } catch (error) {
      console.error("❌ SelfCertEntity Error saving form:", error);
      alert("Error saving form: " + error);
    }
  };

  // Function to handle radio buttons for sole residency
  const handleSoleResidencyChange = (value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      "SC-Entity-Section_2_sole_residency_yes": value ? "true" : "",
      "SC-Entity-Section_2_sole_residency_no": value ? "" : "true",
    }));
  };

  return (
    <div key={componentKey} className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Section I - Identification of the Entity */}
            <section style={sectionStyle}>
              <FormTitle value={"Section I – Identification of the Entity"} />

              {/* UniqueIdentifier Field - Add at the very top */}
              {/* <section style={sectionStyle}> */}
              <FormInput
                label={"Unique Identifier"}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={getFieldValue("UniqueIdentifier")}
                onChange={handleChange}
              />
              {/* </section> */}

              <FormInput
                label="1. Legal name of the Entity or Branch"
                id="SC-Entity-Section_1_entity_name"
                name="SC-Entity-Section_1_entity_name"
                value={getFieldValue("SC-Entity-Section_1_entity_name")}
                onChange={handleChange}
              />

              <CountryDropdown
                label="2. Country of Incorporation or Organization"
                id="SC-Entity-Section_1_org_country"
                name="SC-Entity-Section_1_org_country"
                value={getFieldValue("SC-Entity-Section_1_org_country")}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "SC-Entity-Section_1_org_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <FormsubTitle value={"3. Current Residence Address"} />
              <FormInput
                label="a. Apt/Suite Name/Number/Street"
                id="SC-Entity-Section_1_current_residence_address_street"
                name="SC-Entity-Section_1_current_residence_address_street"
                value={getFieldValue("SC-Entity-Section_1_current_residence_address_street")}
                onChange={handleChange}
              />

              <FormInput
                label="b. Town/City"
                id="SC-Entity-Section_1_current_residence_address_city"
                name="SC-Entity-Section_1_current_residence_address_city"
                value={getFieldValue("SC-Entity-Section_1_current_residence_address_city")}
                onChange={handleChange}
              />

              <FormInput
                label="c. Province/State"
                id="SC-Entity-Section_1_current_residence_address_state"
                name="SC-Entity-Section_1_current_residence_address_state"
                value={getFieldValue("SC-Entity-Section_1_current_residence_address_state")}
                onChange={handleChange}
              />

              <CountryDropdown
                label="d. Country"
                id="SC-Entity-Section_1_current_residence_address_country"
                name="SC-Entity-Section_1_current_residence_address_country"
                value={getFieldValue("SC-Entity-Section_1_current_residence_address_country")}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "SC-Entity-Section_1_current_residence_address_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <FormInput
                label="e. Postal Code"
                id="SC-Entity-Section_1_current_residence_address_postal"
                name="SC-Entity-Section_1_current_residence_address_postal"
                value={getFieldValue("SC-Entity-Section_1_current_residence_address_postal")}
                onChange={handleChange}
              />

              <FormsubTitle value={"4. Mailing Address (if different)"} />
              <FormInput
                label="a. Apt/Suite Name/Number/Street"
                id="SC-Entity-Section_1_mailing_address_street"
                name="SC-Entity-Section_1_mailing_address_street"
                value={getFieldValue("SC-Entity-Section_1_mailing_address_street")}
                onChange={handleChange}
              />

              <FormInput
                label="b. Town/City"
                id="SC-Entity-Section_1_mailing_address_city"
                name="SC-Entity-Section_1_mailing_address_city"
                value={getFieldValue("SC-Entity-Section_1_mailing_address_city")}
                onChange={handleChange}
              />

              <FormInput
                label="c. Province/State"
                id="SC-Entity-Section_1_mailing_address_state"
                name="SC-Entity-Section_1_mailing_address_state"
                value={getFieldValue("SC-Entity-Section_1_mailing_address_state")}
                onChange={handleChange}
              />

              <CountryDropdown
                label="d. Country"
                id="SC-Entity-Section_1_mailing_address_country"
                name="SC-Entity-Section_1_mailing_address_country"
                value={getFieldValue("SC-Entity-Section_1_mailing_address_country")}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "SC-Entity-Section_1_mailing_address_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <FormInput
                label="e. Postal Code"
                id="SC-Entity-Section_1_mailing_address_postal"
                name="SC-Entity-Section_1_mailing_address_postal"
                value={getFieldValue("SC-Entity-Section_1_mailing_address_postal")}
                onChange={handleChange}
              />
            </section>

            {/* Section II - Tax Residency */}
            <section style={sectionStyle}>
              <FormTitle value={"Section II – Tax Residency"} />
              <p>
                I hereby certify that the entity identified above is resident for tax purposes in
                the following jurisdiction(s) and the entity's Taxpayer Identification Number
                ("TIN") or functional equivalent in its jurisdiction of residence for tax purposes
                is:
              </p>

              <CountryDropdown
                label="5. Jurisdiction 1"
                id="SC-Entity-Section_2_tax_identification_country"
                name="SC-Entity-Section_2_tax_identification_country"
                value={getFieldValue("SC-Entity-Section_2_tax_identification_country")}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "SC-Entity-Section_2_tax_identification_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <FormInput
                label="a. Tax reference number type"
                id="SC-Entity-Section_2_tax_reference_number_type"
                name="SC-Entity-Section_2_tax_reference_number_type"
                value={getFieldValue("SC-Entity-Section_2_tax_reference_number_type")}
                onChange={handleChange}
              />

              <FormInput
                label="b. Tax Identification Number"
                id="SC-Entity-Section_2_tax_id"
                name="SC-Entity-Section_2_tax_id"
                value={getFieldValue("SC-Entity-Section_2_tax_id")}
                onChange={handleChange}
              />

              <FormCheckBox
                label="c. Check this box if this jurisdiction does not issue or you are unable to procure a TIN or functional equivalent"
                name="SC-Entity-Section_2_unable_to_procure_a_tin"
                checked={getFieldValue("SC-Entity-Section_2_unable_to_procure_a_tin")}
                onChange={handleChange}
              />

              <FormInput
                label="d. Specify the reason for non-availability of TIN"
                id="SC-Entity-Section_2_reason_for_non_tin"
                name="SC-Entity-Section_2_reason_for_non_tin"
                value={getFieldValue("SC-Entity-Section_2_reason_for_non_tin")}
                onChange={handleChange}
              />

              <FormsubTitle value={"6. Confirmation of Sole Residency *"} />
              <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <label style={{ marginRight: "20px" }}>
                  <input
                    type="radio"
                    name="soleResidency"
                    checked={Boolean(formData["SC-Entity-Section_2_sole_residency_yes"])}
                    onChange={() => handleSoleResidencyChange(true)}
                    style={{ marginRight: "5px" }}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="soleResidency"
                    checked={Boolean(formData["SC-Entity-Section_2_sole_residency_no"])}
                    onChange={() => handleSoleResidencyChange(false)}
                    style={{ marginRight: "5px" }}
                  />
                  No
                </label>
              </div>
              <p>
                I certify that, for the purposes of taxation, the Account Holder is only a tax
                resident in the country listed as Jurisdiction 1.
              </p>

              <FormsubTitle
                value={
                  "7. Additional Jurisdictions of Residency for Tax Purposes (if applicable). If more than four tax residencies, please provide additional attachment."
                }
              />

              {/* Static 3-row table for additional jurisdictions */}
              <div style={{ overflowX: "auto", marginBottom: "20px" }}>
                <table
                  style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Jurisdiction
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Identification Number
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Reference Number Type
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Reason for non-availability of TIN (if applicable)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 1 - Jurisdiction 2 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Jurisdiction of Tax Residence2"
                          name="sc_11 Jurisdiction of Tax Residence2"
                          value={getFieldValue("sc_11 Jurisdiction of Tax Residence2")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Identification Number2"
                          name="sc_11 Tax Identification Number2"
                          value={getFieldValue("sc_11 Tax Identification Number2")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Reference Number Type2"
                          name="sc_11 Tax Reference Number Type2"
                          value={getFieldValue("sc_11 Tax Reference Number Type2")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 non-availability of TIN 2"
                          name="sc_11 non-availability of TIN 2"
                          value={getFieldValue("sc_11 non-availability of TIN 2")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>

                    {/* Row 2 - Jurisdiction 3 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Jurisdiction of Tax Residence3"
                          name="sc_11 Jurisdiction of Tax Residence3"
                          value={getFieldValue("sc_11 Jurisdiction of Tax Residence3")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Identification Number3"
                          name="sc_11 Tax Identification Number3"
                          value={getFieldValue("sc_11 Tax Identification Number3")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Reference Number Type3"
                          name="sc_11 Tax Reference Number Type3"
                          value={getFieldValue("sc_11 Tax Reference Number Type3")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 non-availability of TIN 3"
                          name="sc_11 non-availability of TIN 3"
                          value={getFieldValue("sc_11 non-availability of TIN 3")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>

                    {/* Row 3 - Jurisdiction 4 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Jurisdiction of Tax Residence4"
                          name="sc_11 Jurisdiction of Tax Residence4"
                          value={getFieldValue("sc_11 Jurisdiction of Tax Residence4")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Identification Number4"
                          name="sc_11 Tax Identification Number4"
                          value={getFieldValue("sc_11 Tax Identification Number4")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 Tax Reference Number Type4"
                          name="sc_11 Tax Reference Number Type4"
                          value={getFieldValue("sc_11 Tax Reference Number Type4")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_11 non-availability of TIN 4"
                          name="sc_11 non-availability of TIN 4"
                          value={getFieldValue("sc_11 non-availability of TIN 4")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section III - CRS Classification */}
            <section style={sectionStyle}>
              <FormTitle value={"PART III - Common Reporting Standard"} />

              {/* Section 8: Financial Institution */}
              <div style={{ marginBottom: "20px" }}>
                <FormCheckBox
                  label="8. Financial Institution"
                  name="SC-Entity-Section_3_financial_institution"
                  checked={!!getFieldValue("SC-Entity-Section_3_financial_institution")}
                  onChange={handleChange}
                />

                <FormInput
                  label="Global Intermediary Identification Number (GIIN)"
                  id="SC-Entity-Section_3_fatca_purposes"
                  name="SC-Entity-Section_3_fatca_purposes"
                  value={getFieldValue("SC-Entity-Section_3_fatca_purposes")}
                  onChange={handleChange}
                />

                <FormCheckBox
                  label="a. Reporting Financial Institution under CRS"
                  name="SC-Entity-Section_3_reporting_financial_institution_under_crs"
                  checked={
                    !!getFieldValue("SC-Entity-Section_3_reporting_financial_institution_under_crs")
                  }
                  onChange={handleChange}
                />

                <FormCheckBox
                  label="b. Non-Reporting Financial Institution under CRS."
                  name="SC-Entity-Section_3_non_financial_institution_under_crs"
                  checked={
                    !!getFieldValue("SC-Entity-Section_3_non_financial_institution_under_crs")
                  }
                  onChange={handleChange}
                />

                <div style={{ marginLeft: "20px" }}>
                  <FormCheckBox
                    label="Governmental Entity"
                    name="SC-Entity-Section_3_goverment_entity"
                    checked={!!getFieldValue("SC-Entity-Section_3_goverment_entity")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="International Organization"
                    name="SC-Entity-Section_3_international_org"
                    checked={!!getFieldValue("SC-Entity-Section_3_international_org")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Central Bank"
                    name="SC-Entity-Section_3_central_bank"
                    checked={!!getFieldValue("SC-Entity-Section_3_central_bank")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Broad Participation Retirement Fund"
                    name="SC-Entity-Section_3_broad_participation_retirement_fund"
                    checked={
                      !!getFieldValue("SC-Entity-Section_3_broad_participation_retirement_fund")
                    }
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Narrow Participation Retirement Fund"
                    name="SC-Entity-Section_3_narrow_participation_retirement_fund"
                    checked={
                      !!getFieldValue("SC-Entity-Section_3_narrow_participation_retirement_fund")
                    }
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Pension Fund of a Governmental Entity, International Organization, or Central Bank"
                    name="SC-Entity-Section_3_pension_fund"
                    checked={!!getFieldValue("SC-Entity-Section_3_pension_fund")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Exempt Collective Investment Vehicle"
                    name="SC-Entity-Section_3_exempt_collective_investment"
                    checked={!!getFieldValue("SC-Entity-Section_3_exempt_collective_investment")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Trust whose trustee reports all information required to be reported with respect to all CRS Reportable Accounts"
                    name="SC-Entity-Section_3_trust_trustee"
                    checked={!!getFieldValue("SC-Entity-Section_3_trust_trustee")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Qualified Credit Card Issuer"
                    name="SC-Entity-Section_3_qualified_credit_card_issuer"
                    checked={!!getFieldValue("SC-Entity-Section_3_qualified_credit_card_issuer")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="Other"
                    name="SC-Entity-Section_3_other_entity"
                    checked={!!getFieldValue("SC-Entity-Section_3_other_entity")}
                    onChange={handleChange}
                  />

                  <FormInput
                    label="Specify the type provided in the domestic law:"
                    id="SC-Entity-Section_3_other_entity_specify"
                    name="SC-Entity-Section_3_other_entity_specify"
                    value={getFieldValue("SC-Entity-Section_3_other_entity_specify")}
                    onChange={handleChange}
                  />
                </div>

                <FormCheckBox
                  label="c. Financial Institution resident in a Non-Participating Jurisdiction under CRS (Non-Reporting)."
                  name="SC-Entity-Section_3_non_financial_institution_under_crs_resident"
                  checked={
                    !!getFieldValue(
                      "SC-Entity-Section_3_non_financial_institution_under_crs_resident"
                    )
                  }
                  onChange={handleChange}
                />

                <div style={{ marginLeft: "20px" }}>
                  <FormCheckBox
                    label="i. Investment Entity located in a non-participating jurisdiction under CRS and managed by another Financial Institution1 where a controlling ownership interest is held (directly or indirectly) by a company listed on a stock exchange and subject to disclosure requirements or is a majority owned subsidiary of such a company."
                    name="SC-Entity-Section_3_investment_entity_managed_by_company_listed"
                    checked={
                      !!getFieldValue(
                        "SC-Entity-Section_3_investment_entity_managed_by_company_listed"
                      )
                    }
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="ii. Investment Entity located in a non-participating jurisdiction under CRS and managed by another Financial Institution (other than i. above)"
                    name="SC-Entity-Section_3_investment_entity"
                    checked={!!getFieldValue("SC-Entity-Section_3_investment_entity")}
                    onChange={handleChange}
                  />

                  <div style={{ marginLeft: "20px" }}>
                    <FormCheckBox
                      label="(a) a widely-held, regulated Collective Investment Vehicle (CIV) established as a trust; OR"
                      name="SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ"
                      checked={
                        !!getFieldValue(
                          "SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ"
                        )
                      }
                      onChange={handleChange}
                    />

                    <FormCheckBox
                      label="(b) a pension fund established as a trust"
                      name="SC-Entity-Section_3_investment_entity_managed_by_pension_fund"
                      checked={
                        !!getFieldValue(
                          "SC-Entity-Section_3_investment_entity_managed_by_pension_fund"
                        )
                      }
                      onChange={handleChange}
                    />
                  </div>

                  <FormCheckBox
                    label="iii. Other Investment Entity (other than i. or ii. Above); OR"
                    name="SC-Entity-Section_3_other_investment_entity"
                    checked={!!getFieldValue("SC-Entity-Section_3_other_investment_entity")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="iv. Other Financial Institution, including a Depository Financial Institution, Custodial Institution or Specified Insurance Company"
                    name="SC-Entity-Section_3_non_financial_institution"
                    checked={!!getFieldValue("SC-Entity-Section_3_non_financial_institution")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 9: Active Non-Financial Entity */}
              <div style={{ marginBottom: "20px" }}>
                <FormCheckBox
                  label="9. Active Non-Financial Entity (NFE)"
                  name="SC-Entity-Section_3_active_nfe"
                  checked={!!getFieldValue("SC-Entity-Section_3_active_nfe")}
                  onChange={handleChange}
                />

                <div style={{ marginLeft: "20px" }}>
                  <FormCheckBox
                    label="a. Corporation that is regularly traded or a related entity of a regularly traded corporation."
                    name="SC-Entity-Section_3_regularly_traded_corporation"
                    checked={!!getFieldValue("SC-Entity-Section_3_regularly_traded_corporation")}
                    onChange={handleChange}
                  />

                  <FormInput
                    label="Provide the name of the stock exchange where traded:"
                    id="SC-Entity-Section_3_stock_exchange_name"
                    name="SC-Entity-Section_3_stock_exchange_name"
                    value={getFieldValue("SC-Entity-Section_3_stock_exchange_name")}
                    onChange={handleChange}
                  />

                  <FormInput
                    label="If you are a related entity of a regularly traded corporation, provide the name of the regularly traded corporation:"
                    id="SC-Entity-Section_3_regularly_traded_corporation_name"
                    name="SC-Entity-Section_3_regularly_traded_corporation_name"
                    value={getFieldValue("SC-Entity-Section_3_regularly_traded_corporation_name")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="b. Governmental Entity"
                    name="SC-Entity-Section_3_active_nfe_goverment_entity"
                    checked={!!getFieldValue("SC-Entity-Section_3_active_nfe_goverment_entity")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="c. International Organization"
                    name="SC-Entity-Section_3_active_nfe_international_org"
                    checked={!!getFieldValue("SC-Entity-Section_3_active_nfe_international_org")}
                    onChange={handleChange}
                  />

                  <FormCheckBox
                    label="d. Other Active NFE. Please see attached instructions for relevant definitions."
                    name="SC-Entity-Section_3_active_nfe_other"
                    checked={!!getFieldValue("SC-Entity-Section_3_active_nfe_other")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 10: Passive Non-Financial Entity */}
              <div style={{ marginBottom: "20px" }}>
                <FormCheckBox
                  label="10. Passive Non-Financial Entity."
                  name="SC-Entity-Section_3_passive_nfe"
                  checked={!!getFieldValue("SC-Entity-Section_3_passive_nfe")}
                  onChange={handleChange}
                />
                <p style={{ marginLeft: "20px", fontStyle: "italic" }}>
                  Please see attached instructions for relevant definitions.
                </p>
              </div>

              {/* Section 11: Controlling Persons */}
              <div style={{ marginBottom: "20px" }}>
                <FormsubTitle
                  value={"11. Name(s) of any controlling persons of the account holder"}
                />

                <div
                  style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}
                >
                  <FormInput
                    label="1"
                    id="SC-Entity-Controlling_person_1"
                    name="SC-Entity-Controlling_person_1"
                    value={getFieldValue("SC-Entity-Controlling_person_1")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="2"
                    id="SC-Entity-Controlling_person_2"
                    name="SC-Entity-Controlling_person_2"
                    value={getFieldValue("SC-Entity-Controlling_person_2")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="3"
                    id="SC-Entity-Controlling_person_3"
                    name="SC-Entity-Controlling_person_3"
                    value={getFieldValue("SC-Entity-Controlling_person_3")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="4"
                    id="SC-Entity-Controlling_person_4"
                    name="SC-Entity-Controlling_person_4"
                    value={getFieldValue("SC-Entity-Controlling_person_4")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="5"
                    id="SC-Entity-Controlling_person_5"
                    name="SC-Entity-Controlling_person_5"
                    value={getFieldValue("SC-Entity-Controlling_person_5")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="6"
                    id="SC-Entity-Controlling_person_6"
                    name="SC-Entity-Controlling_person_6"
                    value={getFieldValue("SC-Entity-Controlling_person_6")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="7"
                    id="SC-Entity-Controlling_person_7"
                    name="SC-Entity-Controlling_person_7"
                    value={getFieldValue("SC-Entity-Controlling_person_7")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="8"
                    id="SC-Entity-Controlling_person_8"
                    name="SC-Entity-Controlling_person_8"
                    value={getFieldValue("SC-Entity-Controlling_person_8")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="9"
                    id="SC-Entity-Controlling_person_9"
                    name="SC-Entity-Controlling_person_9"
                    value={getFieldValue("SC-Entity-Controlling_person_9")}
                    onChange={handleChange}
                  />
                  <FormInput
                    label="10"
                    id="SC-Entity-Controlling_person_10"
                    name="SC-Entity-Controlling_person_10"
                    value={getFieldValue("SC-Entity-Controlling_person_10")}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* Section IV - Controlling Persons */}
            <section style={sectionStyle}>
              <FormTitle value={"PART IV - Controlling Persons"} />

              {/* 1) Search Entity */}
              {/* <FormInput
                label="(1) Search Entity:"
                id="SC-Entity-Search_Entity"
                name="SC-Entity-Search_Entity"
                value={getFieldValue('SC-Entity-Search_Entity')}
                  onChange={handleChange}
                />
              <button type="button" style={{ background: '#007bff', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', marginBottom: '20px' }}>
                VIEW DETAILS
              </button> */}

              {/* Personal Information */}
              <FormInput
                label="Title:"
                id="SC-Entity-Controlling_person_title"
                name="SC-Entity-Controlling_person_title"
                value={getFieldValue("SC-Entity-Controlling_person_title")}
                onChange={handleChange}
              />

              <FormInput
                label="Family Name Or Surname:"
                id="SC-Entity-Controlling_person_last_name"
                name="SC-Entity-Controlling_person_last_name"
                value={getFieldValue("SC-Entity-Controlling_person_last_name")}
                onChange={handleChange}
              />

              <FormInput
                label="First or Given Name:"
                id="SC-Entity-Controlling_person_first_name"
                name="SC-Entity-Controlling_person_first_name"
                value={getFieldValue("SC-Entity-Controlling_person_first_name")}
                onChange={handleChange}
              />

              <FormInput
                label="Middle Name:"
                id="SC-Entity-Controlling_person_middle_name"
                name="SC-Entity-Controlling_person_middle_name"
                value={getFieldValue("SC-Entity-Controlling_person_middle_name")}
                onChange={handleChange}
              />

              <FormInput
                label="Please enter the legal name of relevant entity Account Holder(s) of which you are a Controlling Person:"
                id="SC-Entity-Legal_entity"
                name="SC-Entity-Legal_entity"
                value={getFieldValue("SC-Entity-Legal_entity")}
                onChange={handleChange}
              />

              {/* Current Residence Address */}
              <FormsubTitle value={"Current Residence Address"} />
              <FormInput
                label="Property Name/Number:"
                id="SC-Entity-Controlling_person_address_street"
                name="SC-Entity-Controlling_person_address_street"
                value={getFieldValue("SC-Entity-Controlling_person_address_street")}
                onChange={handleChange}
              />

              <FormInput
                label="Street:"
                id="SC-Entity-Controlling_person_address_street2"
                name="SC-Entity-Controlling_person_address_street2"
                value={getFieldValue("SC-Entity-Controlling_person_address_street2")}
                onChange={handleChange}
              />
              <FormInput
                label="Town/City:"
                id="SC-Entity-Controlling_person_address_city"
                name="SC-Entity-Controlling_person_address_city"
                value={getFieldValue("SC-Entity-Controlling_person_address_city")}
                onChange={handleChange}
              />
              <FormInput
                label="Province/State:"
                id="SC-Entity-Controlling_person_address_state"
                name="SC-Entity-Controlling_person_address_state"
                value={getFieldValue("SC-Entity-Controlling_person_address_state")}
                onChange={handleChange}
              />

              <FormInput
                label="Country:"
                id="SC-Entity-Controlling_person_address_country"
                name="SC-Entity-Controlling_person_address_country"
                value={getFieldValue("SC-Entity-Controlling_person_address_country")}
                onChange={handleChange}
              />

              <FormInput
                label="Postal Code:"
                id="SC-Entity-Controlling_person_address_postal"
                name="SC-Entity-Controlling_person_address_postal"
                value={getFieldValue("SC-Entity-Controlling_person_address_postal")}
                onChange={handleChange}
              />

              {/* Mailing Address */}
              <FormsubTitle value={"Mailing address"} />
              <FormInput
                label="Property Name/Number:"
                id="SC-Entity-Controlling_person_mailing_address_street"
                name="SC-Entity-Controlling_person_mailing_address_street"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_street")}
                onChange={handleChange}
              />

              <FormInput
                label="Street:"
                id="SC-Entity-Controlling_person_mailing_address_city"
                name="SC-Entity-Controlling_person_mailing_address_city"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_city")}
                onChange={handleChange}
              />
              <FormInput
                label="Town/City:"
                id="SC-Entity-Controlling_person_mailing_address_city"
                name="SC-Entity-Controlling_person_mailing_address_city"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_city")}
                onChange={handleChange}
              />

              <FormInput
                label="Province/State:"
                id="SC-Entity-Controlling_person_mailing_address_state"
                name="SC-Entity-Controlling_person_mailing_address_state"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_state")}
                onChange={handleChange}
              />

              <FormInput
                label="Country:"
                id="SC-Entity-Controlling_person_mailing_address_country"
                name="SC-Entity-Controlling_person_mailing_address_country"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_country")}
                onChange={handleChange}
              />

              <FormInput
                label="Postal Code:"
                id="SC-Entity-Controlling_person_mailing_address_postal"
                name="SC-Entity-Controlling_person_mailing_address_postal"
                value={getFieldValue("SC-Entity-Controlling_person_mailing_address_postal")}
                onChange={handleChange}
              />

              {/* Date of Birth */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <NormalFormDate
                    label="Date of Birth:"
                    name="SC-Entity-Controlling_person_dob"
                    value={getFieldValue("SC-Entity-Controlling_person_dob")}
                    onChange={handleChange}
                  />
                  {/* <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                    (Enter in U.S. date format: mm/dd/yyyy)
                  </div> */}
                </div>
                {/* <div>
                  <FormInput
                    label="Date of Birth:"
                    id="SC-Entity-Controlling_person_dob_display"
                    name="SC-Entity-Controlling_person_dob_display"
                    value={getFieldValue('SC-Entity-Controlling_person_dob_display')}
                    onChange={handleChange}
                    placeholder="MM/DD/YYYY"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    (As it appears on the Form)
                </div>
                </div> */}
              </div>

              {/* Place of birth */}
              <FormsubTitle value={"Place of birth"} />
              <FormInput
                label="Town/City/Province/State:"
                id="SC-Entity-Controlling_person_city_of_birth"
                name="SC-Entity-Controlling_person_city_of_birth"
                value={getFieldValue("SC-Entity-Controlling_person_city_of_birth")}
                onChange={handleChange}
              />

              <FormInput
                label="Country of Birth"
                id="SC-Entity-Controlling_person_country_of_birth"
                name="SC-Entity-Controlling_person_country_of_birth"
                value={getFieldValue("SC-Entity-Controlling_person_country_of_birth")}
                onChange={handleChange}
              />

              {/* Type of Controlling Person */}
              <FormsubTitle value={"Type of Controlling Person"} />

              <div
                style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}
              >
                <FormCheckBox
                  label="Owner Provide ownership percentage:"
                  name="SC-Entity-Controlling_person_type_owner"
                  checked={!!getFieldValue("SC-Entity-Controlling_person_type_owner")}
                  onChange={handleChange}
                />
                <FormInput
                  label=""
                  id="SC-Entity-Controlling_person_owner_percentage"
                  name="SC-Entity-Controlling_person_owner_percentage"
                  value={getFieldValue("SC-Entity-Controlling_person_owner_percentage")}
                  onChange={handleChange}
                />
                <span>%</span>
              </div>

              <FormCheckBox
                label="Controlling Person by other means"
                name="SC-Entity-Controlling_person_by_other_means"
                checked={!!getFieldValue("SC-Entity-Controlling_person_by_other_means")}
                onChange={handleChange}
              />

              <FormCheckBox
                label="Senior Managing Official:"
                name="SC-Entity-Controlling_person_senior_managing"
                checked={!!getFieldValue("SC-Entity-Controlling_person_senior_managing")}
                onChange={handleChange}
              />

              {/* Trust options */}
              <FormsubTitle value={"In case of a trust please select:"} />
              <FormCheckBox
                label="Settlor"
                name="SC-Entity-Controlling_person_settlor"
                checked={!!getFieldValue("SC-Entity-Controlling_person_settlor")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Trustee"
                name="SC-Entity-Controlling_person_trustee"
                checked={!!getFieldValue("SC-Entity-Controlling_person_trustee")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Protector"
                name="SC-Entity-Controlling_person_protector"
                checked={!!getFieldValue("SC-Entity-Controlling_person_protector")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Beneficiary"
                name="SC-Entity-Controlling_person_beneficiary"
                checked={!!getFieldValue("SC-Entity-Controlling_person_beneficiary")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Other"
                name="SC-Entity-Controlling_person_other"
                checked={!!getFieldValue("SC-Entity-Controlling_person_other")}
                onChange={handleChange}
              />
              <FormInput
                label="If Other, specify:"
                id="SC-Entity-Controlling_person_other_specify"
                name="SC-Entity-Controlling_person_other_specify"
                value={getFieldValue("SC-Entity-Controlling_person_other_specify")}
                onChange={handleChange}
              />

              {/* Legal arrangement options */}
              <FormsubTitle
                value={"In case of a legal arrangement other than a trust please select:"}
              />
              <FormCheckBox
                label="Settlor equivalent"
                name="SC-Entity-Controlling_person_settlor_equivalent"
                checked={!!getFieldValue("SC-Entity-Controlling_person_settlor_equivalent")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Trustee equivalent"
                name="SC-Entity-Controlling_person_trustee_equivalent"
                checked={!!getFieldValue("SC-Entity-Controlling_person_trustee_equivalent")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Protector equivalent"
                name="SC-Entity-Controlling_person_protector_equivalent"
                checked={!!getFieldValue("SC-Entity-Controlling_person_protector_equivalent")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Beneficiary equivalent"
                name="SC-Entity-Controlling_person_beneficiary_equivalent"
                checked={!!getFieldValue("SC-Entity-Controlling_person_beneficiary_equivalent")}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Other equivalent"
                name="SC-Entity-Controlling_person_other_equivalent"
                checked={!!getFieldValue("SC-Entity-Controlling_person_other_equivalent")}
                onChange={handleChange}
              />
              <FormInput
                label="If Other, specify:"
                id="SC-Entity-Controlling_person_other_equivalent_specify"
                name="SC-Entity-Controlling_person_other_equivalent_specify"
                value={getFieldValue("SC-Entity-Controlling_person_other_equivalent_specify")}
                onChange={handleChange}
              />

              {/* Tax Residency Information */}
              <FormsubTitle value={"9. Jurisdiction 1:"} />

              <FormInput
                label="Select"
                id="SC-Entity-Section_4_jurisdiction_1"
                name="SC-Entity-Section_4_jurisdiction_1"
                value={getFieldValue("SC-Entity-Section_4_jurisdiction_1")}
                onChange={handleChange}
              />

              <FormInput
                label="a. Tax reference number type:"
                id="SC-Entity-Section_4_tax_reference_number"
                name="SC-Entity-Section_4_tax_reference_number"
                value={getFieldValue("SC-Entity-Section_4_tax_reference_number")}
                onChange={handleChange}
              />

              <FormInput
                label="b. Tax Identification Number:"
                id="SC-Entity-Section_4_tax_id"
                name="SC-Entity-Section_4_tax_id"
                value={getFieldValue("SC-Entity-Section_4_tax_id")}
                onChange={handleChange}
              />

              <div style={{ marginBottom: 15 }}>
                <FormCheckBox
                  label="c. Check this box if this jurisdiction does not issue or you are unable to procure a TIN or functional equivalent:"
                  name="SC-Entity-Section_4_unable_to_procure_tin"
                  checked={!!getFieldValue("SC-Entity-Section_4_unable_to_procure_tin")}
                  onChange={handleChange}
                />
              </div>

              <FormInput
                label="d. Specify the reason for non-availability of TIN:"
                id="SC-Entity-Section_4_reason_non_availability_of_tin"
                name="SC-Entity-Section_4_reason_non_availability_of_tin"
                value={getFieldValue("SC-Entity-Section_4_reason_non_availability_of_tin")}
                onChange={handleChange}
              />

              <FormsubTitle value={"10. Confirmation of Sole Residency:"} />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 20,
                  marginTop: 10,
                  flexDirection: "row",
                }}
              >
                <p>
                  I certify that for the purposes of taxation, the Account Holder is not a tax
                  resident in any other country than Jurisdiction 1*
                </p>
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"Yes"}
                  name={"SC-Entity-Section_4_sole_residency_yes"}
                  checked={getFieldValue("SC-Entity-Section_4_sole_residency_yes")}
                  onChange={handleChange}
                />
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"No"}
                  name={"SC-Entity-Section_4_sole_residency_no"}
                  checked={getFieldValue("SC-Entity-Section_4_sole_residency_no")}
                  onChange={handleChange}
                />
              </div>

              <FormsubTitle value={"11. Additional Jurisdiction Of Residency for Tax Purposes"} />

              <div style={{ overflowX: "auto", marginBottom: "20px" }}>
                <table
                  style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Jurisdiction of Tax Residence
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Identification Number
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Reference Number Type
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Reason for non-availability of TIN (if applicable)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_jurisdiction_2"
                          name="SC-Entity-Controlling_person_jurisdiction_2"
                          value={getFieldValue("SC-Entity-Controlling_person_jurisdiction_2")}
                          onChange={handleChange}
                          placeholder="Select"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_id_2"
                          name="SC-Entity-Controlling_person_tax_id_2"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_id_2")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_reference_type_2"
                          name="SC-Entity-Controlling_person_tax_reference_type_2"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_reference_type_2")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tin_reason_2"
                          name="SC-Entity-Controlling_person_tin_reason_2"
                          value={getFieldValue("SC-Entity-Controlling_person_tin_reason_2")}
                          onChange={handleChange}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_jurisdiction_3"
                          name="SC-Entity-Controlling_person_jurisdiction_3"
                          value={getFieldValue("SC-Entity-Controlling_person_jurisdiction_3")}
                          onChange={handleChange}
                          placeholder="Select"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_id_3"
                          name="SC-Entity-Controlling_person_tax_id_3"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_id_3")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_reference_type_3"
                          name="SC-Entity-Controlling_person_tax_reference_type_3"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_reference_type_3")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tin_reason_3"
                          name="SC-Entity-Controlling_person_tin_reason_3"
                          value={getFieldValue("SC-Entity-Controlling_person_tin_reason_3")}
                          onChange={handleChange}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_jurisdiction_4"
                          name="SC-Entity-Controlling_person_jurisdiction_4"
                          value={getFieldValue("SC-Entity-Controlling_person_jurisdiction_4")}
                          onChange={handleChange}
                          placeholder="Select"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_id_4"
                          name="SC-Entity-Controlling_person_tax_id_4"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_id_4")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tax_reference_type_4"
                          name="SC-Entity-Controlling_person_tax_reference_type_4"
                          value={getFieldValue("SC-Entity-Controlling_person_tax_reference_type_4")}
                          onChange={handleChange}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="SC-Entity-Controlling_person_tin_reason_4"
                          name="SC-Entity-Controlling_person_tin_reason_4"
                          value={getFieldValue("SC-Entity-Controlling_person_tin_reason_4")}
                          onChange={handleChange}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Signature Section */}

              <FormInput
                label="Signature"
                id="SC-Entity-Section_5_signature"
                name="SC-Entity-Section_5_signature"
                value={getFieldValue("SC-Entity-Section_5_signature")}
                onChange={handleChange}
              />

              <FormInput
                label="Print Name:"
                id="SC-Entity-Section_5_signer_printed_name"
                name="SC-Entity-Section_5_signer_printed_name"
                value={getFieldValue("SC-Entity-Section_5_signer_printed_name")}
                onChange={handleChange}
              />

              <NormalFormDate
                label="Date"
                name="SC-Entity-Section_5_signature_date"
                value={getFieldValue("SC-Entity-Section_5_signature_date")}
                onChange={handleChange}
              />

              <FormInput
                label="Capacity"
                id="SC-Entity-Section_5_capacity"
                name="SC-Entity-Section_5_capacity"
                value={getFieldValue("SC-Entity-Section_5_capacity")}
                onChange={handleChange}
              />

              {/* Action Buttons */}
              {/* <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              
                <button 
                  type="button" 
                  style={{ 
                    background: '#007bff', 
                    color: '#fff', 
                    fontSize: '14px',
                    border: 'none', 
                    fontWeight: 500,
                    padding: '8px 12px', 
                    borderRadius: 4, 
                    cursor: 'pointer' 
                  }}
                >
                  ADD A CONTROLLING PERSON
                </button>
                <button 
                  type="button" 
                  style={{ 
                    background: '#dc3545', 
                    color: '#fff', 
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: 4, 
                    cursor: 'pointer' 
                  }}
                >
                  DELETE THIS CONTROLLING PERSON
                </button>
              </div> */}
            </section>

            {/* Section IV - Declaration and Signature */}
            <section style={sectionStyle}>
              <FormTitle value={"Section IV – Declaration and Signature"} />

              <div className="space-y-4">
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="SC-Entity-isSigned"
                      value="yes"
                      checked={getFieldValue("SC-Entity-isSigned") === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "SC-Entity-isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="SC-Entity-isSigned"
                      value="no"
                      checked={getFieldValue("SC-Entity-isSigned") === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "SC-Entity-isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <b>
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="SC-Entity-isModified"
                      value="yes"
                      checked={getFieldValue("SC-Entity-isModified") === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "SC-Entity-isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="SC-Entity-isModified"
                      value="no"
                      checked={getFieldValue("SC-Entity-isModified") === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "SC-Entity-isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>

                <FormInput
                  label="Print name"
                  id="SC-Entity-Section_4_signer_printed_name"
                  name="SC-Entity-Section_4_signer_printed_name"
                  value={getFieldValue("SC-Entity-Section_4_signer_printed_name")}
                  onChange={handleChange}
                />

                <NormalFormDate
                  label="Date (DD/MM/YYYY)"
                  name="SC-Entity-Section_4_signature_date"
                  value={getFieldValue("SC-Entity-Section_4_signature_date")}
                  onChange={handleChange}
                />

                <FormInput
                  label="If you have signed this on behalf of the Entity, please indicate the capacity in which you have acted here"
                  id="SC-Entity-Section_4_capacity"
                  name="SC-Entity-Section_4_capacity"
                  value={getFieldValue("SC-Entity-Section_4_capacity")}
                  onChange={handleChange}
                />

                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="SC-Entity-Section_4_Signature_unreadable"
                    checked={!!getFieldValue("SC-Entity-Section_4_Signature_unreadable")}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "SC-Entity-Section_4_Signature_unreadable",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label
                    htmlFor="SC-Entity-Section_4_Signature_unreadable"
                    style={{ fontWeight: 500 }}
                  >
                    Check if signature is unreadable
                  </label>
                </div>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#3B82F6",
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SelfCertEntityForm;
